import React, { useState } from 'react';
import axios from '../api/Axios';

const TutorRegisterForm = () => {
  const [formData, setFormData] = useState({
    providerCode: '',
    title: '',
    surname: '',
    otherNames: '',
    email: '',
    password: '',
    confirmPassword: '',
    gender: '',
    studyLevel: '',
    studyLevelNumeric: '',
    category: ''
  });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Password validation function
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }
    
    if (!/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }
    
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      errors.push("Password must contain at least one special character");
    }
    
    return errors;
  };

  // Calculate password strength score
  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) score++;
    return score;
  };

  // Get password strength label and color
  const getPasswordStrengthInfo = (score) => {
    switch (score) {
      case 0:
      case 1:
        return { label: 'Very Weak', color: '#dc3545', width: '20%' };
      case 2:
        return { label: 'Weak', color: '#fd7e14', width: '40%' };
      case 3:
        return { label: 'Fair', color: '#ffc107', width: '60%' };
      case 4:
        return { label: 'Good', color: '#20c997', width: '80%' };
      case 5:
        return { label: 'Strong', color: '#198754', width: '100%' };
      default:
        return { label: '', color: '', width: '0%' };
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updatedData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    // Synchronize studyLevel and studyLevelNumeric
    if (name === 'studyLevel') {
      const qualificationToNumeric = {
        'Bachelors': '1',
        'Honors': '2', 
        'Masters': '3',
        'PhD': '4'
      };
      updatedData.studyLevelNumeric = qualificationToNumeric[value] || '';
    } else if (name === 'studyLevelNumeric') {
      const numericToQualification = {
        '1': 'Bachelors',
        '2': 'Honors',
        '3': 'Masters', 
        '4': 'PhD'
      };
      updatedData.studyLevel = numericToQualification[value] || '';
    }

    setFormData(updatedData);

    // Validate password in real-time
    if (name === 'password') {
      const errors = validatePassword(value);
      setPasswordErrors(errors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');

    // Required fields validation
    const requiredFields = [
      { key: 'providerCode', label: 'Provider Code' },
      { key: 'title', label: 'Title' },
      { key: 'surname', label: 'Surname' },
      { key: 'otherNames', label: 'Other Names' },
      { key: 'email', label: 'Email' },
      { key: 'password', label: 'Password' },
      { key: 'confirmPassword', label: 'Confirm Password' },
      { key: 'gender', label: 'Gender' },
      { key: 'studyLevel', label: 'Highest Qualification Obtained' },
      { key: 'studyLevelNumeric', label: 'Study Level (Numeric)' },
      { key: 'category', label: 'Category' }
    ];

    const missing = requiredFields
      .filter(f => !formData[f.key] || String(formData[f.key]).trim() === '')
      .map(f => f.label);

    if (missing.length > 0) {
      setErrorMsg('Please fill the required fields: ' + missing.join(', '));
      return;
    }

    // Provider code validation (must be exactly 4 alphanumeric characters)
    const providerCodeRegex = /^[A-Z0-9]{4}$/i;
    if (!providerCodeRegex.test(formData.providerCode.trim())) {
      setErrorMsg('Provider code must be exactly 4 alphanumeric characters.');
      return;
    }

    // Basic email format check
    const emailRegex = /\S+@\S+\.\S+/;
    if (!emailRegex.test(formData.email)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    // Password match
    if (formData.password !== formData.confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    // Validate password strength
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setErrorMsg('Please fix the password requirements:\n' + passwordValidationErrors.join('\n'));
      return;
    }

    setLoading(true);

    // Compose payload for Django API
    const payload = {
      user: {
        email: formData.email,
        password: formData.password,
      },
      provider_code: formData.providerCode.toUpperCase().trim(),
      surname: formData.surname,
      other_names: formData.otherNames,
      title: formData.title,
      gender: formData.gender,
      study_level: formData.studyLevel,
      study_level_numeric: formData.studyLevelNumeric,
      subject_category: formData.category
    };

    try {
      await axios.post("accounts/register/tutor-step1/", payload);
      setSuccessMsg("Registration successful! Please check your email to verify your account before logging in.");
      setFormData({
        providerCode: '',
        title: '',
        surname: '',
        otherNames: '',
        email: '',
        password: '',
        confirmPassword: '',
        gender: '',
        studyLevel: '',
        category: ''
      });
    } catch (err) {
      console.log("Full error object:", err);
      console.log("Error response data:", err.response?.data);
      
      if (err.response?.data) {
        // Convert the nested error object to a user-friendly string
        const formatErrors = (errorObj) => {
          const messages = [];
          
          const extractMessages = (obj, prefix = '') => {
            if (Array.isArray(obj)) {
              obj.forEach(msg => messages.push(prefix + msg));
            } else if (typeof obj === 'object' && obj !== null) {
              Object.entries(obj).forEach(([key, value]) => {
                const newPrefix = prefix ? `${prefix} > ${key}: ` : `${key}: `;
                extractMessages(value, newPrefix);
              });
            } else {
              messages.push(prefix + String(obj));
            }
          };
          
          extractMessages(errorObj);
          return messages.join('\\n');
        };
        
        setErrorMsg(formatErrors(err.response.data));
      } else {
        setErrorMsg("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordStrengthInfo = getPasswordStrengthInfo(passwordStrength);

  return (
    <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.17)',
        color: 'rgba(255, 255, 255)',
        padding: '2rem',
        margin: '1rem 0',
        boxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        WebkitBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)',
        MozBoxShadow: '-2px 1px 5px 0px rgba(0,0,0,0.10)'
    }}>
        <form onSubmit={handleSubmit}>
            {/* Provider Code */}
                  {/* ID Document Type */}
                  {/* ID Document Type removed */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Provider Code *</strong>
                </label>
                <input
                    type="text"
                    className="form-control"
                    name="providerCode"
                    value={formData.providerCode}
                    onChange={(e) => {
                        // Convert to uppercase automatically
                        const upperValue = e.target.value.toUpperCase();
                        setFormData({...formData, providerCode: upperValue});
                    }}
                    placeholder="Enter 4-character provider code"
                    maxLength="4"
                    pattern="[A-Z0-9]{4}"
                    style={{textTransform: 'uppercase'}}
                    required
                />
                <small className="text-white-50">
                    Enter the 4-character code provided by your tutoring provider organization
                </small>
            </div>

            {/* Title - Full width */}
            <div className="mb-3">
                <label className="form-label text-white">Title</label>
                <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                placeholder="e.g., Mr, Ms, Dr"
                required
                />
            </div>

            {/* Name fields - Surname first, then Other Names */}
            <div className="row">
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">
                            <strong>Surname *</strong>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            name="surname"
                            value={formData.surname}
                            onChange={handleChange}
                            placeholder="Surname"
                            required
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="mb-3">
                        <label className="form-label text-white">
                            <strong>Other Names *</strong>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            name="otherNames"
                            value={formData.otherNames}
                            onChange={handleChange}
                            placeholder="Other names"
                            required
                        />
                    </div>
                </div>
            </div>

            {/* Gender */}
            <div className="mb-3">
              <label className="form-label text-white">
                <strong>Gender *</strong>
              </label>
              <select
                className="form-control"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
            
              </select>
            </div>

            {/* Highest Qualification Obtained */}
            <div className="mb-3">
              <label className="form-label text-white">Highest Qualification Obtained</label>
              <select
                className="form-control"
                name="studyLevel"
                value={formData.studyLevel}
                onChange={handleChange}
              required
              >
              <option value="">Select Highest Qualification</option>
                <option value="Bachelors">Bachelors</option>
                <option value="Honors">Honors</option>
                <option value="Masters">Masters</option>
                <option value="PhD">PhD</option>
              </select>
            </div>
            {/* Study Level (Numeric) - Hidden but synced */}
            <div className="mb-3" style={{ display: 'none' }}>
              <label className="form-label text-white">Study Level (Numeric)</label>
              <select
                className="form-control"
                name="studyLevelNumeric"
                value={formData.studyLevelNumeric}
                onChange={handleChange}
              required
              >
              <option value="">Select Numeric Value</option>
                <option value="1">1 - Bachelors</option>
                <option value="2">2 - Honors</option>
                <option value="3">3 - Masters</option>
                <option value="4">4 - PhD</option>
              </select>
            </div>

            {/* Category */}
            <div className="mb-3">
              <label className="form-label text-white">
                <strong>Category *</strong>
              </label>
              <select
                className="form-control"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="">Select Category</option>
                <option value="STEM">STEM</option>
                <option value="ARTS">Arts</option>
              </select>
            </div>

            {/* Email Address */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Email Address *</strong>
                </label>
                <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    required
                />
            </div>

            {/* Password */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Password *</strong>
                </label>
                <input
                    type="password"
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    required
                />
                
                {/* Password Requirements */}
                {formData.password && (
                    <div className="mt-2">
                        <div className="mb-2">
                            <div style={{
                                width: '100%',
                                height: '6px',
                                backgroundColor: '#e9ecef',
                                borderRadius: '3px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: passwordStrengthInfo.width,
                                    height: '100%',
                                    backgroundColor: passwordStrengthInfo.color,
                                    transition: 'width 0.3s ease'
                                }}></div>
                            </div>
                            <small className="text-white">
                                Password Strength: <span style={{color: passwordStrengthInfo.color}}>
                                    {passwordStrengthInfo.label}
                                </span>
                            </small>
                        </div>
                        
                        {passwordErrors.length > 0 && (
                            <div>
                                <small className="text-warning">Password Requirements:</small>
                                <ul style={{fontSize: '0.75em', marginLeft: '1rem'}}>
                                    {passwordErrors.map((error, index) => (
                                        <li key={index} className="text-warning">{error}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Confirm Password */}
            <div className="mb-3">
                <label className="form-label text-white">
                    <strong>Confirm Password *</strong>
                </label>
                <input
                    type="password"
                    className="form-control"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    required
                />
            </div>

            {/* Success/Error Messages */}
            {successMsg && (
                <div className="alert alert-success" role="alert">
                    {successMsg}
                </div>
            )}

            {errorMsg && (
                <div className="alert alert-danger" role="alert" style={{whiteSpace: 'pre-line'}}>
                    {errorMsg}
                </div>
            )}

            {/* Submit Button */}
            <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={loading}
            >
                {loading ? 'Registering...' : 'Register as Tutor'}
            </button>

            {/* Login Link */}
            <div className="text-center mt-3">
                <small className="text-white">
                    Already have an account? <a href="/Login" className="text-light">Login here</a>
                </small>
            </div>
        </form>
    </div>
  );
};

export default TutorRegisterForm;
